import React, { useState, useEffect } from 'react';
import { COLORS } from '../../../../core/constants/colors';
import { AdminSubscriptionPlan, CreatePlanRequest, UpdatePlanRequest } from '../types/billing.types';

interface PlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePlanRequest | UpdatePlanRequest) => Promise<void>;
  plan?: AdminSubscriptionPlan | null;
  isLoading?: boolean;
}

export const PlanFormModal: React.FC<PlanFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  plan,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreatePlanRequest>({
    plan_id: '',
    name: '',
    description: '',
    plan_type: '',
    amount: '',
    currency: 'USD',
    duration_days: 30,
    features: [],
    is_active: true,
    is_featured: false,
    sort_order: 0,
  });
  const [featuresInput, setFeaturesInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (plan) {
        // Edit mode
        setFormData({
          plan_id: plan.plan_id,
          name: plan.name,
          description: plan.description,
          plan_type: plan.plan_type,
          amount: plan.amount,
          currency: plan.currency,
          duration_days: plan.duration_days,
          features: plan.features || [],
          is_active: plan.is_active,
          is_featured: plan.is_featured,
          sort_order: plan.sort_order,
        });
        setFeaturesInput(plan.features?.join(', ') || '');
      } else {
        // Create mode
        setFormData({
          plan_id: '',
          name: '',
          description: '',
          plan_type: '',
          amount: '',
          currency: 'USD',
          duration_days: 30,
          features: [],
          is_active: true,
          is_featured: false,
          sort_order: 0,
        });
        setFeaturesInput('');
      }
      setErrors({});
      setError('');
      setHasSubmitted(false);
    }
  }, [isOpen, plan]);

  // Auto-generate plan_id from plan_type + name
  const generatePlanId = (planType: string, name: string): string => {
    if (!planType) return '';
    const slug = name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    return slug ? `${planType}_${slug}` : planType;
  };

  // Update plan_id whenever plan_type or name changes (create mode only)
  useEffect(() => {
    if (!plan) {
      setFormData(prev => ({ ...prev, plan_id: generatePlanId(prev.plan_type, prev.name) }));
    }
  }, [formData.plan_type, formData.name, plan]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.plan_type) {
      newErrors.plan_type = 'Plan type is required';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a valid positive number';
    }

    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    }

    if (!formData.duration_days || formData.duration_days <= 0) {
      newErrors.duration_days = 'Duration must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setError('');

    if (!validateForm()) {
      return;
    }

    // Parse features from comma-separated string
    const features = featuresInput
      .split(',')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);

    try {
      const submitData = { ...formData, features };
      
      if (plan) {
        // Update mode - remove plan_id from update request
        const { plan_id, ...updateData } = submitData;
        await onSubmit(updateData);
      } else {
        // Create mode
        await onSubmit(submitData);
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save plan');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Drawer */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} style={{ maxHeight: '100vh', overflowY: 'auto' }}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              {plan ? 'Edit Plan' : 'Create New Plan'}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              disabled={isLoading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Plan Type *
                  </label>
                  <select
                    value={formData.plan_type}
                    onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.plan_type ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.plan_type ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading || !!plan}
                  >
                    <option value="">Select type</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  {hasSubmitted && errors.plan_type && (
                    <p className="text-xs text-red-500 mt-1">{errors.plan_type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.name ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.name ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                    placeholder="Premium Monthly"
                  />
                  {hasSubmitted && errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Auto-generated Plan ID (read-only) */}
              {formData.plan_id && (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                    Plan ID (auto-generated)
                  </label>
                  <input
                    type="text"
                    value={formData.plan_id}
                    readOnly
                    className="w-full px-4 py-2.5 rounded-lg border bg-gray-50 cursor-not-allowed text-sm"
                    style={{ borderColor: '#E5E7EB', color: '#6B7280' }}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    hasSubmitted && errors.description ? 'border-red-500' : ''
                  }`}
                  style={{ borderColor: hasSubmitted && errors.description ? '#DC2626' : '#E5E7EB' }}
                  disabled={isLoading}
                  placeholder="Full access to all premium features"
                />
                {hasSubmitted && errors.description && (
                  <p className="text-xs text-red-500 mt-1">{errors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Amount *
                  </label>
                  <input
                    type="text"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.amount ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.amount ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                    placeholder="29.99"
                  />
                  {hasSubmitted && errors.amount && (
                    <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Currency *
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.currency ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.currency ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="ZAR">ZAR</option>
                    <option value="KES">KES</option>
                    <option value="NGN">NGN</option>
                  </select>
                  {hasSubmitted && errors.currency && (
                    <p className="text-xs text-red-500 mt-1">{errors.currency}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Duration *
                  </label>
                  <select
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.duration_days ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.duration_days ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days (Monthly)</option>
                    <option value={90}>90 days (Quarterly)</option>
                    <option value={180}>180 days (Semi-Annual)</option>
                    <option value={365}>365 days (Annual)</option>
                  </select>
                  {hasSubmitted && errors.duration_days && (
                    <p className="text-xs text-red-500 mt-1">{errors.duration_days}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Features (comma-separated)
                </label>
                <input
                  type="text"
                  value={featuresInput}
                  onChange={(e) => setFeaturesInput(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: '#E5E7EB' }}
                  disabled={isLoading}
                  placeholder="Feature 1, Feature 2, Feature 3"
                />
                <p className="text-xs text-gray-500 mt-1">Separate features with commas</p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: COLORS.admin.primary }}
                      disabled={isLoading}
                    />
                    <label htmlFor="is_active" className="text-sm font-medium" style={{ color: '#1F2937' }}>
                      Active
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: COLORS.admin.primary }}
                      disabled={isLoading}
                    />
                    <label htmlFor="is_featured" className="text-sm font-medium" style={{ color: '#1F2937' }}>
                      Featured
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium flex items-center gap-1" style={{ color: '#1F2937' }}>
                    Sort Order
                    <span className="relative group">
                      <svg className="w-4 h-4 cursor-help" style={{ color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-gray-800 rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity">
                        Controls display order on the pricing page. Lower numbers appear first.
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-20 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                    style={{ borderColor: '#E5E7EB' }}
                    disabled={isLoading}
                    min="0"
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-5 border-t flex gap-4 flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg border font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              style={{ borderColor: '#E5E7EB' }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: COLORS.admin.primary }}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
